import React, { useCallback, useState, useEffect } from 'react';
import ModalWrapper from '@components/wrapper/Modal/ModalWrapper';
import { ModalInner } from '@components/wrapper/Modal/ModalStyle';
import CloseBtn from '@components/dataEntry/button/CloseBtn/CloseBtn';
import Button from '@components/dataEntry/button/Button';
import TextInput from '@components/dataEntry/Input/TextInput';
import { useInputs } from '@hooks/useInputs';
import CustomUploadBox from '@components/dataEntry/upload/CustomUploadBox';
import { CollectionPath } from '@typings/collections.enum';
import { UserPet } from '@typings/pets/pet.interface';
import { useCollection, fuego } from '@nandorojo/swr-firestore';
import { useRecoilValue } from 'recoil';
import { currentUserIDState } from '@atom/setting/user';
import { IstoreItem } from '@typings/store/storeItem.interface';
import styled from 'styled-components';
import { updateImgAndGetURL } from '@store/firebase/storage';
import Label from '@components/dataEntry/Input/Label';
import { deleteItemsFromInventory } from '@store/firebase/store/parchase-item';
import { addItemHistory, ItemHistoryType } from '@store/firebase/store/addItemHistory';
import { IPhotoCard } from '@typings/photocard/photocard.interface';
import { usePetLabel } from '@pages/Pets/hooks/usePetLabel';

type DeleteImageProps = {
  collectionPath: string;
  docId: string;
  imgURL: string;
  key: string;
  selectedFile: File | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  photocardData?: IPhotoCard;
  ownerID: string;
  targetItemData: IstoreItem | any;
  options: 'appearance' | 'name' | 'background';
};

function ChangePetModal({
  open,
  onClose,
  photocardData,
  ownerID,
  targetItemData,
  options,
}: Props) {
  const { petLabel } = usePetLabel();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedBgFile, setSelectedBgFile] = useState<File | null>(null);
  const [{ petName }, handleChange, reset] = useInputs({
    petName: targetItemData?.customName || '',
  });
  const [selectedPet, setSelectedPet] = useState<UserPet | null>(null);

  const handleDeleteImage = useCallback(
    ({
      collectionPath,
      docId,
      imgURL,
      key,
      selectedFile,
    }: DeleteImageProps) => {
      if (selectedFile) {
        // deleteImage({ collectionPath, docId, imgURL, key });
      }
    },
    [],
  );

  const { id: photocardID, path: photocardPath } = photocardData || {};

  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(async () => {
    if (!selectedPet) {
      alert(`${petLabel}을(를) 선택해주세요.`);
      return;
    }

    // 옵션별 유효성 검사
    if (options === 'name' && !petName.trim()) {
      alert(`${petLabel} 이름을 입력해주세요.`);
      return;
    }
    if (options === 'appearance' && !selectedFile) {
      alert(`${petLabel} 이미지를 선택해주세요.`);
      return;
    }
    if (options === 'background' && !selectedBgFile) {
      alert('배경 이미지를 선택해주세요.');
      return;
    }

    try {
      setSaving(true);
      const docRef = fuego.db
        .collection(`${CollectionPath.USERS}/${ownerID}/pets`)
        .doc(selectedPet.id!);

      if (options === 'name') {
        await docRef.set({ customName: petName, updatedAt: new Date() }, {
          merge: true,
        } as any);
      }

      if (options === 'appearance') {
        const imgURL = await updateImgAndGetURL({
          selectedFile: selectedFile!,
          prevImgURL: '', // 기존 이미지를 삭제해 펫 템플릿에 영향을 끼치지 않기 위해 빈 값으로 설정.
          collectionPath: `${CollectionPath.USERS}/${ownerID}/pets`,
          deletePrevURL: true,
        } as any);
        if (!imgURL) {
          alert('이미지 업로드에 실패했습니다.');
          return;
        }
        const levelKey = `level${selectedPet.level}`;
        await docRef.set(
          {
            legacyPetImgList: [
              ...(selectedPet.legacyPetImgList || []),
              { levelKey, imgURL },
            ],
            customImgURL: imgURL,
            updatedAt: new Date(),
          },
          { merge: true } as any,
        );
      }

      if (options === 'background') {
        const bgURL = await updateImgAndGetURL({
          selectedFile: selectedBgFile!,
          prevImgURL: selectedPet.backgroundImgURL || '',
          collectionPath: `${CollectionPath.USERS}/${ownerID}/pets`,
          deletePrevURL: true,
        } as any);
        if (!bgURL) {
          alert('배경 이미지 업로드에 실패했습니다.');
          return;
        }
        await docRef.set({ backgroundImgURL: bgURL, updatedAt: new Date() }, {
          merge: true,
        } as any);
      }

      // 내 인벤토리에서 아이템 삭제
      if (targetItemData && photocardID) {
        // photocardPath가 이미 'photocard'와 같은 컬렉션 경로면 그대로 사용
        // 아니면 CollectionPath.PHOTOCARD 사용
        const basePath = photocardPath || CollectionPath.PHOTOCARD;
        const inventoryPath = `${basePath}/${photocardID}/inventory`;
        await deleteItemsFromInventory({
          data: targetItemData,
          amount: 1,
          collectionPath: inventoryPath,
        });

        if (ownerID) {
          const optionLabel = options === 'name' ? '이름 변경' : options === 'appearance' ? '외관 변경' : '배경 변경';
          addItemHistory({
            uid: ownerID,
            itemName: targetItemData.title || '',
            itemId: targetItemData.originItemID || targetItemData.id || '',
            type: ItemHistoryType.USE_PET,
            count: -1,
            memo: `${petLabel} ${optionLabel} (${selectedPet?.customName || ''})`,
          }).catch(console.error);
        }
      }

      onClose();
    } catch (error) {
      console.error(`${petLabel} 정보 변경 실패:`, error);
      alert(`${petLabel} 정보 변경에 실패했습니다. 다시 시도해주세요.`);
    } finally {
      setSaving(false);
    }
  }, [
    selectedPet,
    petName,
    petLabel,
    selectedFile,
    selectedBgFile,
    ownerID,
    options,
    onClose,
    targetItemData,
    photocardID,
    photocardPath,
  ]);

  const petsColPath = `${CollectionPath.USERS}/${ownerID}/pets`;
  const { data: ownedPets } = useCollection<UserPet>(petsColPath, {
    listen: false,
    orderBy: ['createdAt', 'desc'],
  });

  // 첫 로드 시 기본 선택 (있다면 첫번째)
  useEffect(() => {
    if (!selectedPet && Array.isArray(ownedPets) && ownedPets.length > 0) {
      setSelectedPet(ownedPets[0]);
    }
  }, [ownedPets, selectedPet]);

  if (!open) return null;
  return (
    <ModalWrapper outsideClick={onClose} contentStyle={{ maxWidth: 420 }}>
      <ModalInner>
        <CloseBtn fixed onClick={onClose} size="md" />

        <h6>{petLabel} 정보 변경</h6>

        {/* 내가 가진 펫 중 선택 UI */}
        <Label>{petLabel} 선택</Label>
        <PetList role="radiogroup" aria-label="펫 선택">
          {(ownedPets || []).map((pet) => {
            const active = selectedPet?.id === pet.id;
            return (
              <PetCard
                key={pet.id}
                $active={!!active}
                onClick={() => setSelectedPet(pet)}
                type="button"
                role="radio"
                aria-checked={active}
                aria-label={pet.customName || pet.petTemplateId}
              >
                <PetThumb>
                  {pet.customImgURL ? (
                    <img src={pet.customImgURL} alt={pet.customName} />
                  ) : (
                    <span>🐾</span>
                  )}
                </PetThumb>
                <PetName title={pet.customName}>{pet.customName}</PetName>
              </PetCard>
            );
          })}
        </PetList>

        {options === 'name' && (
          <TextInput
            label={`${petLabel} 이름`}
            name="petName"
            value={petName}
            onChange={handleChange}
            placeholder={`${petLabel} 이름`}
          />
        )}

        {options === 'appearance' && (
          <CustomUploadBox
            label={`${petLabel} 이미지`}
            setSelectedFile={setSelectedFile}
            prevImgURL={selectedPet?.customImgURL}
            customDelete={() =>
              handleDeleteImage({
                collectionPath: petsColPath,
                docId: selectedPet?.id || '',
                imgURL: selectedPet?.customImgURL || '',
                key: 'imgURL',
                selectedFile,
              })
            }
            id={`pet-img-${selectedPet?.id}`}
            accept="image/*"
          />
        )}

        {options === 'background' && (
          <CustomUploadBox
            label={`${petLabel} 배경 이미지`}
            setSelectedFile={setSelectedBgFile}
            prevImgURL={selectedPet?.backgroundImgURL}
            customDelete={() =>
              handleDeleteImage({
                collectionPath: petsColPath,
                docId: selectedPet?.id || '',
                imgURL: selectedPet?.backgroundImgURL || '',
                key: 'imgURL',
                selectedFile,
              })
            }
            id={`pet-bg-img-${selectedPet?.id}`}
            accept="image/*"
          />
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button onClick={onClose} shape="stroke">
            취소
          </Button>
          <Button
            onClick={handleSave}
            disabled={!selectedPet || saving}
            pending={saving}
          >
            저장
          </Button>
        </div>
      </ModalInner>
    </ModalWrapper>
  );
}

export default ChangePetModal;

// styles
const SectionTitle = styled.h6`
  margin: 8px 0;
`;

const PetList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
`;

const PetCard = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid
    ${({ $active }) => ($active ? 'var(--primaryColor)' : 'var(--lineColor)')};
  background: var(--cardBackgroundColor);
  color: var(--textColor);
  cursor: var(--hoverCursorURL), pointer;
  transition: border-color 0.2s ease, transform 0.1s ease;
  &:hover {
    transform: translateY(-1px);
  }
`;

const PetThumb = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 8px;
  background: var(--inkColor);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`;

const PetName = styled.div`
  font-size: 12px;
  text-align: center;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PreviewWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 8px 0 12px 0;
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PreviewRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
`;

const PreviewCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PreviewLabel = styled.div`
  font-size: 12px;
  color: var(--subTextColor);
`;

const PreviewImg = styled.div`
  width: 100%;
  height: 120px;
  border: 1px solid var(--lineColor);
  border-radius: var(--cardBorderRadius);
  background: var(--inkColor);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`;

const PreviewBg = styled(PreviewImg)`
  img {
    object-fit: cover;
  }
`;
